import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Loader2, Mic, MicOff, Send, Check, X, Edit2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { AuthenticatedPdfViewer } from './AuthenticatedPdfViewer';

const GhostDealBuilder = ({ open, onClose, onDealCreated, clientContext }) => {
  const navigate = useNavigate();
  const [sessionId] = useState(() => crypto.randomUUID());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState(null);
  const [creating, setCreating] = useState(false);
  const [dealCreated, setDealCreated] = useState(null);
  const [createError, setCreateError] = useState(null);
  const chatEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  // Set initial message based on context
  useEffect(() => {
    if (open && messages.length === 0) {
      if (clientContext?.full_name) {
        setMessages([{
          role: 'assistant',
          content: `I see you're working with **${clientContext.full_name}**.\n\nI already have their info:\n- Email: ${clientContext.email || 'not on file'}\n- Phone: ${clientContext.phone || 'not on file'}\n- Current deal: ${clientContext.deal_stage || 'none'}\n\nWhat would you like to do? Add a new deal, update their timeshare details, or create a proposal?`
        }]);
      } else {
        setMessages([{
          role: 'assistant',
          content: "Ready to enter a new deal? Tell me the client's name and their timeshares — I'll check our database first.\n\nExample: \"I have Larry Brown with 2 deeds, Wyndham Williamsburg and Hilton Orlando\""
        }]);
      }
    }
  }, [open, clientContext]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim()) return;
    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await api.post('/ghost-deal/chat', {
        message: text,
        session_id: sessionId,
        client_context: clientContext || null,
      });
      const assistantMsg = { role: 'assistant', content: res.data.response };
      setMessages(prev => [...prev, assistantMsg]);

      if (res.data.extracted_data?.status === 'ready') {
        setExtractedData(res.data.extracted_data);
        setEditData(JSON.parse(JSON.stringify(res.data.extracted_data)));
      }
    } catch (err) {
      toast.error('Ghost could not respond');
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I had trouble processing that. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  // Voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => chunksRef.current.push(e.data);
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const formData = new FormData();
        formData.append('audio', blob, 'recording.webm');

        setLoading(true);
        try {
          const res = await api.post('/ghost-deal/transcribe', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          if (res.data.text) {
            sendMessage(res.data.text);
          }
        } catch {
          toast.error('Voice transcription failed');
          setLoading(false);
        }
      };

      mediaRecorder.start();
      setRecording(true);
    } catch {
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  // Create deal from extracted data
  // v3.21-1715 · propagate existing_client_id so backend NEVER creates
  // a duplicate client when Ghost is working against a confirmed DB match.
  const handleCreateDeal = async () => {
    if (!editData) return;
    setCreating(true);
    setCreateError(null);
    // Prefer clientContext (opened from a client page) → guaranteed real
    // UUID.  Otherwise use the LLM-provided existing_client_id ONLY when
    // it looks like a real UUID (belt-and-suspenders; backend also rejects).
    const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const candidate =
      clientContext?.id ||
      editData?.existing_client_id ||
      editData?.client?.existing_client_id ||
      null;
    const existing_client_id = candidate && uuidRe.test(String(candidate).trim())
      ? String(candidate).trim() : null;
    try {
      const res = await api.post('/ghost-deal/create-deal', {
        session_id: sessionId,
        client: editData.client,
        deeds: editData.deeds,
        pricing: editData.pricing_suggestion,
        existing_client_id,
      });
      setDealCreated(res.data);
      toast.success(
        res.data.is_existing_client
          ? `Deal ${res.data.deal_number} added to ${res.data.full_name}`
          : `New client + deal created for ${res.data.full_name}`
      );
      if (onDealCreated) onDealCreated(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to create deal';
      setCreateError(detail);
      toast.error(detail);
    } finally {
      setCreating(false);
    }
  };

  // v3.21-1716 · Proposal-generation flow removed.  Ghost only builds
  // the Deal; the operator opens the Deal Workspace to Preview/Create/
  // Send the authoritative Client Info and Service Authorization.

  // v3.21-1716 · Preview Service Authorization = open the REAL
  // authenticated Client Info and Service Authorization PDF via the
  // in-app viewer.  We no longer use the legacy HTML "Proposal
  // Preview" panel — that flow is decommissioned.
  const [previewOpen, setPreviewOpen] = useState(false);
  const openServiceAuthPreview = () => {
    if (!dealCreated?.client_id || !dealCreated?.deal_id) return;
    setPreviewOpen(true);
  };

  const updateDeed = (idx, field, value) => {
    setEditData(prev => {
      const d = { ...prev };
      d.deeds = [...d.deeds];
      d.deeds[idx] = { ...d.deeds[idx], [field]: value };
      return d;
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0" data-testid="ghost-deal-builder">
        <DialogHeader className="px-4 py-3 border-b">
          <DialogTitle className="text-sm font-bold flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            Ghost Deal Builder
          </DialogTitle>
        </DialogHeader>

        {/* Chat area */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-[300px] max-h-[400px]" data-testid="ghost-chat">
          {messages.map((msg, i) => (
            <div key={`msg-${i}-${msg.role}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-lg px-3 py-2 text-xs leading-relaxed whitespace-pre-wrap ${
                msg.role === 'user' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700'
              }`}>
                {msg.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-slate-100 rounded-lg px-3 py-2">
                <Loader2 className="h-3 w-3 animate-spin text-slate-400" />
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Extracted data review */}
        {extractedData && !dealCreated && (
          <div className="border-t bg-slate-50 px-4 py-3 space-y-2" data-testid="ghost-extracted-data">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-700">Deal Summary</span>
              <Button size="sm" variant="ghost" className="h-5 text-[10px]" onClick={() => setEditMode(!editMode)}>
                <Edit2 className="h-3 w-3 mr-1" />{editMode ? 'Done Editing' : 'Edit'}
              </Button>
            </div>

            {/* Client */}
            <div className="grid grid-cols-3 gap-2">
              {editMode ? (
                <>
                  <Input className="h-6 text-xs" value={editData?.client?.full_name || ''} onChange={e => setEditData(prev => ({ ...prev, client: { ...prev.client, full_name: e.target.value } }))} placeholder="Name" />
                  <Input className="h-6 text-xs" value={editData?.client?.email || ''} onChange={e => setEditData(prev => ({ ...prev, client: { ...prev.client, email: e.target.value } }))} placeholder="Email" />
                  <Input className="h-6 text-xs" value={editData?.client?.phone || ''} onChange={e => setEditData(prev => ({ ...prev, client: { ...prev.client, phone: e.target.value } }))} placeholder="Phone" />
                </>
              ) : (
                <>
                  <span className="text-xs"><span className="text-slate-400">Name:</span> {editData?.client?.full_name}</span>
                  <span className="text-xs"><span className="text-slate-400">Email:</span> {editData?.client?.email || '-'}</span>
                  <span className="text-xs"><span className="text-slate-400">Phone:</span> {editData?.client?.phone || '-'}</span>
                </>
              )}
            </div>

            {/* Deeds */}
            {editData?.deeds?.map((deed, i) => (
              <div key={deed.resort_name || `deed-${i}`} className="bg-white border rounded p-2">
                <span className="text-[10px] text-slate-400 uppercase">Deed {i + 1}</span>
                {editMode ? (
                  <div className="grid grid-cols-3 gap-1 mt-1">
                    <Input className="h-6 text-xs" value={deed.resort_name || ''} onChange={e => updateDeed(i, 'resort_name', e.target.value)} placeholder="Resort" />
                    <Input className="h-6 text-xs" value={deed.resort_location || ''} onChange={e => updateDeed(i, 'resort_location', e.target.value)} placeholder="Location" />
                    <Input className="h-6 text-xs" value={deed.ownership_type || ''} onChange={e => updateDeed(i, 'ownership_type', e.target.value)} placeholder="Type" />
                    <Input className="h-6 text-xs" type="number" value={deed.maintenance_fee || ''} onChange={e => updateDeed(i, 'maintenance_fee', Number(e.target.value))} placeholder="Maint. Fee" />
                    <Input className="h-6 text-xs" type="number" value={deed.balance_owed || ''} onChange={e => updateDeed(i, 'balance_owed', Number(e.target.value))} placeholder="Balance" />
                  </div>
                ) : (
                  <div className="text-xs mt-1 space-y-0.5">
                    <span className="font-medium">{deed.resort_name}</span> — {deed.resort_location}
                    <div className="text-slate-500">Type: {deed.ownership_type} | Maint: ${(deed.maintenance_fee || 0).toLocaleString()} | Balance: ${(deed.balance_owed || 0).toLocaleString()}</div>
                  </div>
                )}
              </div>
            ))}

            {/* Pricing */}
            <div className="bg-white border rounded p-2">
              <span className="text-[10px] text-slate-400 uppercase">Pricing</span>
              {editMode ? (
                <div className="grid grid-cols-2 gap-1 mt-1">
                  <Input className="h-6 text-xs" type="number" value={editData?.pricing_suggestion?.total_deal_amount || ''} onChange={e => setEditData(prev => ({ ...prev, pricing_suggestion: { ...prev.pricing_suggestion, total_deal_amount: Number(e.target.value) } }))} placeholder="Total Deal Amount" />
                  <Input className="h-6 text-xs" type="number" value={editData?.pricing_suggestion?.per_deed_fee || ''} onChange={e => setEditData(prev => ({ ...prev, pricing_suggestion: { ...prev.pricing_suggestion, per_deed_fee: Number(e.target.value) } }))} placeholder="Per Deed Fee" />
                </div>
              ) : (
                <div className="text-xs mt-1">
                  <span className="font-bold text-emerald-700">${(editData?.pricing_suggestion?.total_deal_amount || 0).toLocaleString()}</span>
                  <span className="text-slate-400 ml-2">(${(editData?.pricing_suggestion?.per_deed_fee || 0).toLocaleString()} per deed)</span>
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 flex-1" onClick={handleCreateDeal} disabled={creating} data-testid="ghost-create-deal-btn">
                {creating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Check className="h-3 w-3 mr-1" />}
                {creating ? 'Creating...' : 'Confirm & Create Deal'}
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setExtractedData(null); setEditData(null); sendMessage("Let me make some changes to the deal details."); }}>
                <X className="h-3 w-3 mr-1" />Back to Chat
              </Button>
            </div>
          </div>
        )}

        {/* v3.21-1715 · Deal created success panel — shows REAL ids and
            hands the operator directly into the corrected workflow. */}
        {dealCreated && (
          <div className="border-t bg-emerald-50 px-4 py-3 space-y-2" data-testid="ghost-deal-created">
            <div className="flex items-center gap-2 flex-wrap">
              <Check className="h-4 w-4 text-emerald-600" />
              <span className="text-xs font-bold text-emerald-700">
                {dealCreated.is_existing_client ? 'Deal added to existing client' : 'Client + Deal created'}
              </span>
              <Badge className="text-[9px] bg-emerald-100 text-emerald-700" data-testid="ghost-deal-amount">
                ${(dealCreated.deal_amount || 0).toLocaleString()}
              </Badge>
              {dealCreated.deal_stage && (
                <Badge variant="outline" className="text-[9px]" data-testid="ghost-deal-stage">
                  {String(dealCreated.deal_stage).replace(/_/g, ' ')}
                </Badge>
              )}
            </div>
            <div className="text-[10px] text-emerald-900 leading-relaxed bg-white/70 rounded p-2 border border-emerald-200 font-mono">
              <div data-testid="ghost-result-client">Client: <span className="font-semibold">{dealCreated.full_name}</span> · <span className="text-slate-500">{dealCreated.client_id}</span></div>
              <div data-testid="ghost-result-deal">Deal: <span className="font-semibold">{dealCreated.deal_number}</span> · <span className="text-slate-500">{dealCreated.deal_id}</span></div>
              <div data-testid="ghost-result-ownerships">Ownerships: <span className="font-semibold">{dealCreated.ownership_ids?.length || 0}</span>{dealCreated.ownership_ids?.length ? ` · ${dealCreated.ownership_ids[0].slice(0,8)}…` : ''}</div>
              {dealCreated.next_action && (
                <div data-testid="ghost-result-next-action">Next: <span className="font-semibold">{dealCreated.next_action}</span></div>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => { navigate(`/ptc-deals/${dealCreated.deal_id}`); onClose(); }}
                      data-testid="ghost-view-deal-btn">
                Open Deal Workspace
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs"
                      onClick={openServiceAuthPreview}
                      data-testid="ghost-preview-service-auth-btn">
                <FileText className="h-3 w-3 mr-1" />
                Preview Service Authorization
              </Button>
              {/* v3.21-1716 · "Build Closing Packet" is gated inside the
                  Deal Workspace once prerequisites are met.  We no
                  longer expose it as a raw shortcut from Ghost. */}
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onClose} data-testid="ghost-done-btn">Done</Button>
            </div>
          </div>
        )}
        {createError && !dealCreated && (
          <div className="border-t bg-red-50 px-4 py-2 text-[11px] text-red-700" data-testid="ghost-create-error">
            <span className="font-semibold">Create failed:</span> {createError}
          </div>
        )}

        {/* v3.21-1716 · Authenticated PDF viewer replaces the legacy
            HTML Proposal Preview panel.  Opens the REAL populated CISA
            PDF via bearer-authenticated fetch. */}
        {dealCreated && (
          <AuthenticatedPdfViewer
            open={previewOpen}
            onOpenChange={setPreviewOpen}
            title="Client Info and Service Authorization — Preview"
            description={`${dealCreated.full_name} · ${dealCreated.deal_number}`}
            fetchPath={`/clients/${dealCreated.client_id}/deals/${dealCreated.deal_id}/service-auth/preview.pdf?mark_needs_input=true`}
            filename={`ServiceAuth_${dealCreated.deal_number}.pdf`}
          />
        )}

        {/* Input area */}
        {!dealCreated && (
          <div className="border-t px-4 py-2 flex gap-2" data-testid="ghost-input">
            <Input
              className="flex-1 h-8 text-xs"
              placeholder="Type or speak to Ghost..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              data-testid="ghost-text-input"
            />
            <Button size="sm" variant={recording ? 'destructive' : 'outline'} className="h-8 w-8 p-0" onClick={recording ? stopRecording : startRecording} disabled={loading} data-testid="ghost-mic-btn">
              {recording ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
            </Button>
            <Button size="sm" className="h-8 w-8 p-0" onClick={() => sendMessage(input)} disabled={loading || !input.trim()} data-testid="ghost-send-btn">
              <Send className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GhostDealBuilder;
